import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Bookmark, X, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, STAGES } from "@/lib/api";

export const EMPTY_FILTERS = { q: "", stage: [], category: [], source: [], campaign: "all", owner: "all", score_min: "", score_max: "", has_website: "any", has_phone: "any", has_email: "any", contacted: "any", replied: "any" };

export const toParams = (f) => {
  const p = {};
  Object.entries(f).forEach(([k, v]) => {
    if (Array.isArray(v)) { if (v.length) p[k] = v.join(","); }
    else if (v !== "" && v !== "all" && v !== "any") p[k] = v;
  });
  return p;
};

export const activeCount = (f) => Object.keys(toParams({ ...f, q: "" })).length;

function MultiPick({ label, options, value, onChange, testid }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`justify-between min-w-[150px] ${value.length ? "border-blue-400 text-blue-700" : ""}`} data-testid={testid}>
          {label}{value.length > 0 && <span className="ml-1.5 bg-blue-600 text-white rounded-full px-1.5 text-[10px]">{value.length}</span>}<ChevronDown size={14} className="ml-2 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="max-h-72 overflow-y-auto space-y-0.5">
          {options.map((o) => (
            <label key={o.value} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm" data-testid={`${testid}-opt-${o.value}`}>
              <Checkbox checked={value.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
              <span className="flex-1 truncate">{o.label}</span>{o.n != null && <span className="font-mono text-xs text-slate-400">{o.n}</span>}
            </label>
          ))}
          {!options.length && <div className="p-2 text-xs text-slate-500">Nothing to filter yet</div>}
        </div>
        {value.length > 0 && <Button size="sm" variant="ghost" className="w-full mt-1" onClick={() => onChange([])} data-testid={`${testid}-clear`}>Clear</Button>}
      </PopoverContent>
    </Popover>
  );
}

function Pick({ value, onChange, items, testid, className = "w-44" }) {
  return (
    <Select value={value} onValueChange={onChange}><SelectTrigger className={className} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
  );
}

function SavedViews({ filters, onApply }) {
  const [views, setViews] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/lead-views").then((r) => setViews(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/lead-views", { name, filters }); toast.success(`Saved view "${name}"`); setName(""); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { await api.delete(`/lead-views/${id}`); load(); };
  return (
    <Popover>
      <PopoverTrigger asChild><Button variant="outline" data-testid="saved-views-btn"><Bookmark size={14} className="mr-1" />Saved views{views.length > 0 && ` (${views.length})`}</Button></PopoverTrigger>
      <PopoverContent className="w-80 p-3 space-y-3" align="end">
        <div className="space-y-1 max-h-60 overflow-y-auto">
          {views.map((v) => (
            <div key={v.id} className="flex items-center gap-1">
              <button className="flex-1 text-left text-sm px-2 py-1.5 rounded hover:bg-blue-50" onClick={() => onApply({ ...EMPTY_FILTERS, ...v.filters })} data-testid={`apply-view-${v.id}`}>{v.name}</button>
              <button className="p-1 text-slate-400 hover:text-red-600" onClick={() => del(v.id)} data-testid={`delete-view-${v.id}`}><X size={14} /></button>
            </div>
          ))}
          {!views.length && <div className="text-xs text-slate-500">No saved views yet. Set your filters, then save them here.</div>}
        </div>
        <div className="flex gap-2 border-t pt-3">
          <Input placeholder="Name this view" value={name} onChange={(e) => setName(e.target.value)} data-testid="save-view-name-input" />
          <Button disabled={!name.trim()} onClick={save} data-testid="save-view-btn">Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function LeadFilters({ f, setF, camps, users, refresh = 0 }) {
  const [facets, setFacets] = useState({ categories: [], sources: [] });
  const [more, setMore] = useState(false);
  useEffect(() => { api.get("/leads/facets").then((r) => setFacets(r.data)); }, [refresh]);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const yn = (any, yes, no) => [["any", any], ["yes", yes], ["no", no]];
  const n = activeCount(f);
  return (
    <div className="space-y-3" data-testid="lead-filters">
      <div className="flex flex-wrap gap-2 items-center">
        <MultiPick label="Category" testid="filter-category" value={f.category} onChange={set("category")}
          options={facets.categories.map((c) => ({ value: c.value, label: c.value === "__none__" ? "(No category)" : c.value, n: c.n }))} />
        <MultiPick label="Stage" testid="filter-stage" value={f.stage} onChange={set("stage")} options={STAGES.map((s) => ({ value: s.key, label: `${s.n} · ${s.label}` }))} />
        <Pick value={f.campaign} onChange={set("campaign")} testid="filter-campaign" className="w-52"
          items={[["all", "All campaigns"], ["none", "No campaign"], ...camps.map((c) => [c.id, c.name])]} />
        <MultiPick label="Source" testid="filter-source" value={f.source} onChange={set("source")} options={facets.sources.map((s) => ({ value: s.value, label: s.value, n: s.n }))} />
        <Button variant="ghost" onClick={() => setMore(!more)} data-testid="more-filters-btn"><SlidersHorizontal size={14} className="mr-1" />{more ? "Fewer filters" : "More filters"}</Button>
        {n > 0 && <Button variant="ghost" className="text-red-600" onClick={() => setF({ ...EMPTY_FILTERS, q: f.q })} data-testid="clear-filters-btn"><X size={14} className="mr-1" />Clear {n}</Button>}
        <div className="ml-auto"><SavedViews filters={f} onApply={setF} /></div>
      </div>
      {more && (
        <div className="flex flex-wrap gap-2 items-center bg-slate-50 border rounded-xl p-3" data-testid="more-filters-panel">
          <Pick value={f.owner} onChange={set("owner")} testid="filter-owner" items={[["all", "Any owner"], ["me", "My leads"], ...users.map((u) => [u.id, u.name])]} />
          <div className="flex items-center gap-1"><span className="label-xs">Score</span>
            <Input className="w-20" type="number" placeholder="min" value={f.score_min} onChange={(e) => set("score_min")(e.target.value)} data-testid="filter-score-min" />
            <Input className="w-20" type="number" placeholder="max" value={f.score_max} onChange={(e) => set("score_max")(e.target.value)} data-testid="filter-score-max" /></div>
          <Pick value={f.has_website} onChange={set("has_website")} testid="filter-has-website" items={yn("Website: any", "Has website", "No website")} />
          <Pick value={f.has_email} onChange={set("has_email")} testid="filter-has-email" items={yn("Email: any", "Has email", "No email")} />
          <Pick value={f.has_phone} onChange={set("has_phone")} testid="filter-has-phone" items={yn("Phone: any", "Has phone", "No phone")} />
          <Pick value={f.contacted} onChange={set("contacted")} testid="filter-contacted" items={yn("Contacted: any", "Contacted", "Never contacted")} />
          <Pick value={f.replied} onChange={set("replied")} testid="filter-replied" items={yn("Replied: any", "Replied", "Not replied")} />
        </div>
      )}
    </div>
  );
}
